import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faGlobe, faUser } from '@fortawesome/pro-light-svg-icons';
import { Button, SecondaryButton } from '../display/button';
import { Modal } from '../feedback/modal';
import { trpc } from '../../utils/trpc-client';
import { TableType, SavedViewScope } from 'shared/src/domain/saved-view/saved-view-types';
import { useStore } from '../../store/store';
import { useFlagEnabled } from '@/utils/feature-flags';

type Props = {
  tableType: TableType;
  getCurrentViewState?: () => Record<string, any>;
  defaultQuery?: string;
};

export function SaveViewButton({ tableType, getCurrentViewState, defaultQuery }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [scope, setScope] = useState<SavedViewScope>('personal');
  const [hasStateChanged, setHasStateChanged] = useState(false);
  const urlTick = useStore(state => state.urlTick);
  const canCreateGlobalViews = useFlagEnabled('saved-views-create-global');
  const utils = trpc.useUtils();

  const createViewMutation = trpc.createSavedView.useMutation({
    onSuccess: async () => {
      setModalOpen(false);
      setViewName('');
      setScope('personal');
      await utils.savedViews.invalidate();
    }
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const compressedState = searchParams.get('state');
    const searchQuery = searchParams.get('search');
    setHasStateChanged(!!(compressedState || searchQuery || defaultQuery));
  }, [urlTick, defaultQuery]);

  function handleSave() {
    if (!viewName.trim() || !getCurrentViewState) return;

    const currentViewState = getCurrentViewState();

    createViewMutation.mutate({
      name: viewName.trim(),
      tableType,
      scope,
      viewState: currentViewState
    });
  }

  function handleModalOpen() {
    // Reset scope to personal if user can't create global views
    if (!canCreateGlobalViews && scope === 'global') {
      setScope('personal');
    }
    setModalOpen(true);
  }

  if (!hasStateChanged) {
    return null;
  }

  return (
    <>
      <div className="w-28">
        <Button onClick={handleModalOpen} size="compact" className="w-full">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSave} />
            <div>Save View</div>
          </div>
        </Button>
      </div>

      <Modal
        open={modalOpen}
        setOpen={setModalOpen}
        header={
          <div className="rounded-t-2xl bg-blue-50 py-6 text-center">
            <FontAwesomeIcon icon={faSave} className="mb-2 text-2xl text-blue-600" />
            <h3 className="text-xl font-semibold text-gray-900">Save Current View</h3>
          </div>
        }
        content={
          <div className="space-y-4 p-6">
            <div>
              <label htmlFor="view-name" className="mb-2 block text-sm font-medium text-gray-700">
                View Name
              </label>
              <input
                type="text"
                id="view-name"
                value={viewName}
                onChange={e => setViewName(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter a name for this view"
                maxLength={255}
              />
            </div>

            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">View Type</label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="scope"
                    value="personal"
                    checked={scope === 'personal'}
                    onChange={e => setScope(e.target.value as SavedViewScope)}
                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3 flex items-center">
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                    <div className="flex flex-col text-sm font-medium text-gray-700">
                      <div>Personal View</div>
                      <p className="text-xs text-gray-500">Only visible to you</p>
                    </div>
                  </div>
                </label>

                {canCreateGlobalViews && (
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="scope"
                      value="global"
                      checked={scope === 'global'}
                      onChange={e => setScope(e.target.value as SavedViewScope)}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex items-center">
                      <FontAwesomeIcon icon={faGlobe} className="mr-2 text-gray-500" />
                      <div className="flex flex-col text-sm font-medium text-gray-700">
                        <div>Global View</div>
                        <p className="text-xs text-gray-500">Visible to all users</p>
                      </div>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {createViewMutation.error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{createViewMutation.error.message}</div>
              </div>
            )}
          </div>
        }
        footer={
          <div className="flex gap-4 p-4">
            <SecondaryButton className="grow justify-around" onClick={() => setModalOpen(false)}>
              Cancel
            </SecondaryButton>
            <Button
              className="grow justify-around"
              onClick={handleSave}
              disabled={!viewName.trim() || createViewMutation.isPending}>
              {createViewMutation.isPending ? 'Saving...' : 'Save View'}
            </Button>
          </div>
        }
      />
    </>
  );
}
